import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { OutputEditRequest, OutputEditResponse, OutputEvidenceResponse, WorkProductDetail, WorkProductFamily } from "../../../shared/types";

export function useOutputDetail(family: WorkProductFamily | undefined, key: string | undefined) {
  return useQuery({
    queryKey: ["output-detail", family, key],
    queryFn: () =>
      api.get<{ data: WorkProductDetail }>(
        `/api/outputs/${encodeURIComponent(family!)}/${encodeURIComponent(key!)}`,
      ),
    enabled: !!family && !!key,
    staleTime: 10_000,
  });
}

export function useEditOutput(family: WorkProductFamily | undefined, key: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: OutputEditRequest) => {
      if (!family || !key) throw new Error("Missing output identity");
      return api.patch<{ data: OutputEditResponse }>(
        `/api/outputs/${encodeURIComponent(family)}/${encodeURIComponent(key)}`,
        body,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["output-detail", family, key] });
    },
  });
}

export function useOutputEvidence(family: WorkProductFamily | undefined, key: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["output-evidence", family, key],
    queryFn: () =>
      api.get<{ data: OutputEvidenceResponse }>(
        `/api/outputs/${encodeURIComponent(family!)}/${encodeURIComponent(key!)}/evidence`,
      ),
    enabled: enabled && !!family && !!key,
    staleTime: 10_000,
  });
}

export function useRenderedOutputPreview(
  brandId: string | undefined,
  content: string | null | undefined,
  context: "html" | "plain",
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["output-render-preview", brandId, context, content],
    queryFn: () =>
      api.post<{
        data: {
          resolved: string;
          refs: Array<{ asset_id: string; url: string | null; unresolved?: boolean; alt?: string }>;
        };
      }>(
        `/api/brands/${brandId}/render`,
        { text: content ?? "", context },
      ),
    enabled: enabled && !!brandId && !!content?.trim(),
    staleTime: 10_000,
  });
}
